import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import useMeta from "../hooks/useMeta";
import { api } from "../lib/api";
import { renderMarkdown, formatPostDate } from "../lib/markdown";

const Shell = ({ children, testid }) => (
  <div data-testid={testid}>
    <Nav />
    <div style={{ background: "var(--charcoal)", height: 88 }} />
    <section className="section" style={{ background: "var(--ivory)", minHeight: "60vh" }}>{children}</section>
    <Footer />
  </div>
);

export function BlogIndex() {
  useMeta("Blog | Saviar", "Notes on soft living, wellness, and intentional routines from Saviar.");
  const [posts, setPosts] = useState(null);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)).catch(() => setPosts([])); }, []);

  return (
    <Shell testid="blog-index-page">
      <div className="container-x" style={{ maxWidth: 880 }}>
        <p className="eyebrow" style={{ marginBottom: 16 }}>The Saviar Journal</p>
        <h1 className="display" style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.4rem)", color: "var(--charcoal)", marginBottom: 56 }}>Blog</h1>
        {posts === null ? <p className="body-sm">Loading…</p> : posts.length === 0 ? (
          <p className="body-lg" data-testid="blog-empty">New writing is on its way. Please check back soon.</p>
        ) : (
          <div style={{ display: "grid", gap: 56 }} data-testid="blog-list">
            {posts.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} style={{ textDecoration: "none", color: "inherit", display: "grid", gridTemplateColumns: p.cover_image_url ? "minmax(0, 260px) 1fr" : "1fr", gap: 32, alignItems: "center", borderTop: "1px solid var(--border-gold)", paddingTop: 40 }} className="blog-card" data-testid={`blog-card-${p.slug}`}>
                {p.cover_image_url && <img src={p.cover_image_url} alt={p.title} style={{ width: "100%", aspectRatio: "4/3", objectFit: "cover" }} />}
                <div>
                  <p className="eyebrow" style={{ marginBottom: 10 }}>{formatPostDate(p.publish_date)}</p>
                  <h2 style={{ fontFamily: "var(--serif)", fontWeight: 300, fontSize: "clamp(1.5rem, 2.6vw, 2rem)", color: "var(--charcoal)", margin: "0 0 12px", lineHeight: 1.2 }}>{p.title}</h2>
                  {p.excerpt && <p className="body-sm" style={{ margin: 0 }}>{p.excerpt}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);
  useMeta(post ? `${post.title} | Saviar` : missing ? "Post not found | Saviar" : "Blog | Saviar", post?.excerpt);
  useEffect(() => {
    setPost(null); setMissing(false);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setMissing(true));
  }, [slug]);

  return (
    <Shell testid="blog-post-page">
      <article className="container-x" style={{ maxWidth: 760 }}>
        <Link to="/blog" className="btn-ghost" style={{ display: "inline-block", marginBottom: 40 }} data-testid="blog-back">All posts</Link>
        {missing ? <p className="body-lg" data-testid="blog-not-found">This post isn't available.</p> : !post ? <p className="body-sm">Loading…</p> : (
          <>
            <p className="eyebrow" style={{ marginBottom: 14 }} data-testid="blog-post-date">{formatPostDate(post.publish_date)}</p>
            <h1 className="display" style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.4rem)", color: "var(--charcoal)", marginBottom: 32 }} data-testid="blog-post-title">{post.title}</h1>
            {post.cover_image_url && <img src={post.cover_image_url} alt={post.title} style={{ width: "100%", maxHeight: 520, objectFit: "cover", marginBottom: 40 }} data-testid="blog-post-cover" />}
            <div data-testid="blog-post-body">{renderMarkdown(post.body)}</div>
          </>
        )}
      </article>
    </Shell>
  );
}
